import type { TagColor } from '@arhs-fleetback/types/entities/tags';
import type { ConfigProviderProps } from 'antd/es/config-provider';

import { css } from 'config/emotionCss';
import colors from 'config/theme/colors';

import { ALL_ROLES } from 'constants/UserConstants';

const STATUS_SENT_BG = '#C1E4FB';
const STATUS_SENT_COLOR = '#0E6199';

// One entry per role that has a colour of its own; ALL_ROLES members absent here fall back below.
const ROLE_COLORS: Record<string, string> = {
  admin: '#B71C1C',
  manufacturer: '#880E4F',
  group_manager: '#4A148C',
  key_deposit_manager: '#1976D2',
  back_office: '#00ACC1',
  salesman: '#2E7D32',
  vo_stock_manager: '#4CAF50',
  audit_manager: '#E65100',
  auditor: '#F57C00',
  technician: '#C2185B',
  after_sales_manager: '#610c2d',
  sales_manager: '#163e19',
  parts_manager: '#312E81',
  parts_officer: '#6366F1',
  campaign_manager: '#32ADE6',
  campaign_officer: '#2581AC',
  self_service_manager: '#E54F84',
  self_service_officer: '#EF77A5',
};

const UNKNOWN_ROLE_BG = '#e5e7e8';

const roleStyles = ALL_ROLES.reduce((acc, ROLE) => {
  const role = ROLE.toLowerCase();
  const background = ROLE_COLORS[role];

  return `${acc}
  &.${role} {
    background-color: ${background ?? UNKNOWN_ROLE_BG};
    color: ${background ? '#FFF' : colors['@text-color-secondary']};
  }
`;
}, '');

const TAG_COLORS: Record<TagColor, { color: string; background: string }> = {
  red: { color: '#D32B3E', background: '#FDE7E6' },
  blue: { color: '#3A92D9', background: '#EAF4FB' },
  green: { color: '#52C41A', background: '#F6FFED' },
  orange: { color: '#DD7F02', background: '#FFF6C6' },
  purple: { color: '#7472B9', background: '#E9ECF6' },
  magenta: { color: '#9C5D9A', background: '#F7EFF8' },
  pink: { color: '#D42E61', background: '#FBE8F0' },
  grey: { color: '#90959A', background: colors['@body-bg'] },
};

const colorStyles = Object.entries(TAG_COLORS).reduce((acc, [name, { color, background }]) => `${acc}
  &.tag-${name} {
    color: ${color};
    background-color: ${background};
    border: 1px solid ${color};
  }
`, '');

const rootStyle = css`
  border: 0;
  font-weight: 400;
  letter-spacing: -0.5px;
  line-height: 18px;
  padding: 4px 8px;
  text-align: center;
  text-transform: none;
  vertical-align: middle;
  user-select: none;
  text-overflow: ellipsis;
  overflow: hidden;
  max-width: 100%;

  /* The status tags on dashboard cards. Both values used to ride two utility classes that the audit
     removed with the rest of _tag.less: .rounded for the corner and small__tag for the leading.
     Stated here rather than at the call sites so the tag owns its own look, and as a compound
     selector so it outranks the root without an !important. */
  &.tag--status {
    border-radius: 4px;
    line-height: 19px;
  }

  &.l {
    padding: 7px 12px;
  }

  &.xl {
    padding: 8px 16px;
  }

  &.lg {
    font-size: 21px !important;
    line-height: 1.25 !important;
    padding: 4px 0.75em;
    border-radius: 5px !important;
  }

  &.modes.technician,
  &.default {
    color: ${colors['@white']};
    background-color: ${colors['@primary-color']};
  }

  &.draft,
  &.aborted,
  &.stopped,
  &.stock_vo_open {
    color: #2f3f4f;
    background-color: #d7e4f4;
  }

  &.free,
  &.failed,
  &.created,
  &.upcoming,
  &.to_be_sent,
  &.appointment-open,
  &.campaignFile-pending,
  &.planned {
    background-color: #fad7e6;
    color: #b2004a;
  }

  &.sent,
  &.to_prepare {
    background-color: ${STATUS_SENT_BG};
    color: ${STATUS_SENT_COLOR};
  }

  &.read,
  &.received,
  &.used,
  &.not_planned {
    background-color: #cad0e9;
    color: #1c234f;
  }

  &.opened {
    background-color: #e9ecf6;
    color: #1c234f;
  }

  &.to_start {
    background-color: #e9ecf6;
    color: #6561a1;
  }

  &.mobile,
  &.sending,
  &.clicked,
  &.answered {
    background-color: #ebd8ec;
    color: #6f1b7a;
  }

  &.closed,
  &.disabled,
  &.archived,
  &.appointment-closed,
  &.deleted {
    background-color: #e5e7e8;
    color: ${colors['@text-color-secondary']};
  }

  &.paid,
  &.deposit_paid,
  &.pre_authorized,
  &.payment-succeed {
    background-color: #f8f1dd;
    color: #88732e;
  }

  &.ended,
  &.to_check,
  &.accepted,
  &.active,
  &.success,
  &.published,
  &.open {
    background-color: ${colors['@success-color-light']};
    color: ${colors['@success-color-dark']};
  }

  &.error,
  &.refused {
    background-color: ${colors['@error-color-light']};
    color: ${colors['@error-color-dark']};
  }

  &.web,
  &.preapproved {
    color: ${colors['@primary-color-dark']};
    background-color: ${colors['@item-hover-bg']};
  }

  &.pending {
    color: #ff6000;
    background-color: #ffe9da;
  }

  ${roleStyles}

  ${colorStyles}

  &.check_in-sent {
    background-color: rgba(221, 107, 32, 0.1);
    color: ${colors['@checkIn-completed']};

    span {
      color: ${colors['@checkIn-completed']};

      &.self-service-icon:before {
        color: ${colors['@checkIn-completed']};
      }
    }
  }

  &.check_in-answered,
  &.breakdown-answered {
    background-color: ${colors['@checkIn-completed']};
    color: ${colors['@white']};

    span {
      color: ${colors['@white']};

      &.self-service-icon:before {
        color: ${colors['@white']};
      }
    }
  }

  &.check_out-sent {
    background-color: rgba(57, 72, 92, 0.1);
    color: ${colors['@checkOut-completed']};

    span {
      color: ${colors['@checkOut-completed']};

      &.self-service-icon:before {
        color: ${colors['@checkOut-completed']};
      }
    }
  }

  &.check_out-answered,
  &.check_out-manual {
    background-color: ${colors['@checkOut-completed']};
    color: ${colors['@white']};

    span {
      color: ${colors['@white']};

      &.self-service-icon:before {
        color: ${colors['@white']};
      }
    }
  }

  &.final-invoice-sent {
    color: #dcb746;
    background: rgba(220, 183, 70, 0.1);
  }

  /* Between sent and paid: funds authorized with the provider, not yet captured. */
  &.final-invoice-pre_authorized {
    color: #88732e;
    background: rgba(220, 183, 70, 0.35);
  }

  &.final-invoice-paid {
    color: ${colors['@white']};
    background: #dcb746;
  }

  &.key_deposit {
    background-color: ${colors['@key-deposit']};
    color: ${colors['@white']};
  }

  &.key_retrieve {
    background-color: ${colors['@key-retrieve']};
    color: ${colors['@white']};
  }

`;

const tagConfig: ConfigProviderProps['tag'] = {
  className: rootStyle,
};

export default tagConfig;
